import React, { useState } from 'react';
import { Buddy } from '../models/Buddy';
import './EggColorSelector.css';

function EggColorSelector({ selectedAnimal, onConfirmBuddy, onBack }) {
  const [selectedColor, setSelectedColor] = useState('blue');
  const [isRolling, setIsRolling] = useState(false);
  const [rolledResult, setRolledResult] = useState(null);
  const [showEggReveal, setShowEggReveal] = useState(false);

  const selectableColors = Buddy.getSelectableEggColors();
  const animalConfig = selectedAnimal ? 
    { key: selectedAnimal, ...Buddy.getSelectableAnimals().find(a => a.key === selectedAnimal) } : 
    null;

  const handleLuckyRoll = () => {
    setIsRolling(true);
    setShowEggReveal(true);
    
    // Simulate egg cracking animation
    setTimeout(() => {
      const result = Buddy.rollRandomBuddy();
      setRolledResult(result);
      setIsRolling(false);
    }, 2000);
  };

  const handleConfirmNormalSelection = () => {
    onConfirmBuddy({
      animalType: selectedAnimal,
      eggColor: selectedColor,
      rarity: 'common',
      isRandomRoll: false
    });
  };

  const handleConfirmRolledBuddy = () => {
    if (rolledResult) {
      onConfirmBuddy(rolledResult);
    } else {
      // User rolled but got normal result - use their selected color
      handleConfirmNormalSelection();
    }
  };

  const getRarityBadge = (rarity) => {
    if (rarity === 'mythical') return '⭐ MYTHICAL';
    if (rarity === 'rare') return '💎 RARE';
    return '';
  };

  return (
    <div className="egg-selector-overlay">
      <div className="egg-selector-modal">
        {!showEggReveal ? (
          <>
            <button className="back-button" onClick={onBack}>
              ← Back
            </button>
            
            <h2>Choose Your Egg Color</h2>
            <p className="egg-selector-subtitle">
              Your {animalConfig?.name} will hatch from this special egg
            </p>

            <div className="selected-animal-preview">
              <span className="preview-emoji">{animalConfig?.emojis[0]}</span>
              <span className="preview-arrow">→</span>
              <span className="preview-emoji">{animalConfig?.emojis[5]}</span>
            </div>

            <div className="color-grid">
              {selectableColors.map((color) => (
                <button
                  key={color.key}
                  className={`color-card ${selectedColor === color.key ? 'selected' : ''}`}
                  onClick={() => setSelectedColor(color.key)}
                  style={{ '--color-hex': color.hex }}
                >
                  <div 
                    className="color-circle" 
                    style={{ backgroundColor: color.hex }}
                  />
                  <div className="color-name">{color.name}</div>
                </button>
              ))}
            </div>

            <div className="button-group">
              <button 
                className="confirm-button"
                onClick={handleConfirmNormalSelection}
              >
                Confirm Selection
              </button>
              
              <div className="divider">
                <span>OR</span>
              </div>

              <button 
                className="lucky-button"
                onClick={handleLuckyRoll}
              >
                <span className="lucky-icon">🎲</span>
                <span>Feeling Lucky?</span>
                <span className="lucky-hint">Try for rare animals!</span>
              </button>
            </div>

            <div className="rarity-info">
              <div className="rarity-tier">
                <span className="rarity-badge grey">⚪ Common</span>
                <span className="rarity-chance">1/10 chance</span>
              </div>
              <div className="rarity-tier">
                <span className="rarity-badge rare">💎 Rare</span>
                <span className="rarity-chance">1/100 chance</span>
              </div>
              <div className="rarity-tier">
                <span className="rarity-badge mythical">⭐ Mythical</span>
                <span className="rarity-chance">1/500 chance</span>
              </div>
            </div>
          </>
        ) : (
          <div className="egg-reveal-container">
            {isRolling ? (
              <>
                <h2>🥚 Cracking the Mystery Egg...</h2>
                <div className="egg-animation">
                  <span className="rolling-egg">🥚</span>
                </div>
                <p className="rolling-text">Something magical is happening...</p>
              </>
            ) : (
              <>
                {rolledResult ? (
                  <>
                    <div className="reveal-success">
                      <h2 className="reveal-title">
                        {getRarityBadge(rolledResult.rarity)}
                      </h2>
                      <div className="revealed-buddy">
                        <span className="revealed-emoji">
                          {Buddy.getSelectableAnimals().find(a => a.key === rolledResult.animalType)?.emojis[5] ||
                           (() => {
                             const animalData = {
                               alien: '⭐🛸👽',
                               eldritchEntity: '⭐👁️🌀',
                               timeDragon: '⭐⏰🐉',
                               phoenix: '⭐🔥🦅',
                               spaceOwl: '🌌🦉',
                               crystalFox: '⭐💎🦊',
                               rabbit: '✨🐰',
                               turtle: '💎🐢',
                               frog: '✨🐸'
                             };
                             return animalData[rolledResult.animalType] || '✨';
                           })()}
                        </span>
                      </div>
                      <p className="revealed-name">
                        You got a {rolledResult.eggColor} egg with a special companion!
                      </p>
                      <button 
                        className="confirm-button"
                        onClick={handleConfirmRolledBuddy}
                      >
                        Start Your Journey!
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h2>No Special Roll This Time</h2>
                    <p className="no-roll-text">
                      But your {animalConfig?.name} is still amazing! 🌟
                    </p>
                    <div className="revealed-buddy">
                      <span className="revealed-emoji">{animalConfig?.emojis[5]}</span>
                    </div>
                    <button 
                      className="confirm-button"
                      onClick={handleConfirmRolledBuddy}
                    >
                      Start Your Journey!
                    </button>
                    <button 
                      className="try-again-button"
                      onClick={() => {
                        setShowEggReveal(false);
                        setRolledResult(null);
                      }}
                    >
                      Try Another Roll
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EggColorSelector;
